import React from "react";
import {Tag} from "primereact/tag";
import {toDateTimeString} from "../../utils/TimeUtil";

// Buybox campaigns only: what the bidirectional controller is currently doing for
// this product. Without this, "deliberately not competing right now" (waiting for
// a floor-breaking competitor to run out, holding at the top, or waiting for a
// pushed price to show up live on the marketplace) is indistinguishable from a
// stuck/broken product -- this is the primary signal support will be asked about.
// Shared by the product list column (Product.js) and the product detail page
// (ProductReprice.js) so the two surfaces cannot drift apart.
export const BUYBOX_STATE_META = {
    holding: {labelKey: 'product.buybox_state_holding', severity: 'success'},
    climbing: {labelKey: 'product.buybox_state_climbing', severity: 'info'},
    chasing: {labelKey: 'product.buybox_state_chasing', severity: 'warning'},
    waiting: {labelKey: 'product.buybox_state_waiting', severity: 'warning'},
    settling: {labelKey: 'product.buybox_state_settling', severity: 'secondary'},
    unwinnable: {labelKey: 'product.buybox_state_unwinnable', severity: 'warning'},
};

// Builds the full label (base label + absolute deadline / indefinite / pending
// suffix) for a buybox_state, or null when there is nothing to show. Both
// waiting_until and settling_until are absolute, nullable timestamps computed
// server-side -- never recomputed or estimated here, and never rendered as a
// relative countdown (it goes stale the moment the tab is left open).
export const buyboxStateLabel = (t, buyboxState) => {
    const meta = buyboxState && buyboxState.state && BUYBOX_STATE_META[buyboxState.state];
    if (!meta)
        return null;

    let label = t(meta.labelKey);
    if (buyboxState.state === 'waiting') {
        // Indefinite patience (waiting_until === null) is the DEFAULT case, not an
        // edge case -- it means the controller will wait until the competitor
        // withdraws, with no deadline.
        label += ' · ' + (buyboxState.waiting_until
            ? t('product.buybox_state_until', {date: toDateTimeString(buyboxState.waiting_until)})
            : t('product.buybox_state_indefinite'));
    } else if (buyboxState.state === 'settling') {
        // Null settling_until means the pushed price has not been observed live on
        // the marketplace yet -- there is no ETA to show, so say what is being
        // waited on instead of rendering an empty, zeroed, or guessed time.
        label += ' · ' + (buyboxState.settling_until
            ? t('product.buybox_state_until', {date: toDateTimeString(buyboxState.settling_until)})
            : t('product.buybox_state_pending'));
    } else if (buyboxState.state === 'unwinnable') {
        // Written off as unwinnable: price is not what decides this buybox, so the
        // controller retreated to just under the rival and stopped chasing. Null
        // unwinnable_until means the backend has not recorded a write-off moment --
        // show the holding text with no retry time rather than a fabricated one.
        label += ' · ' + t('product.buybox_state_unwinnable_holding');
        if (buyboxState.unwinnable_until) {
            label += ' · ' + t('product.buybox_state_unwinnable_retry',
                {date: toDateTimeString(buyboxState.unwinnable_until)});
        }
    }
    return label;
};

// Renders nothing when buybox_state is absent or its state is empty -- true for
// most products, which are not driven by this controller. No empty badge, no
// placeholder.
//
// `compact` (list column) shows the base state label only -- no detail suffix,
// no timestamps -- so a long sentence never wraps a table row. The full text is
// still available via the native `title` tooltip on hover, so nothing is lost,
// only deferred. Default (full, detail page) shows the label plus the detail
// suffix and timestamps exactly as before. Both read from the same
// BUYBOX_STATE_META / buyboxStateLabel above, so the state->label/severity
// mapping cannot fork between the two presentations.
const BuyboxStateBadge = ({buyboxState, t, compact = false}) => {
    const meta = buyboxState && buyboxState.state && BUYBOX_STATE_META[buyboxState.state];
    if (!meta)
        return null;

    if (compact) {
        const fullLabel = buyboxStateLabel(t, buyboxState);
        return (
            <span title={fullLabel}>
                <Tag severity={meta.severity} value={t(meta.labelKey)} />
            </span>
        );
    }

    return <Tag severity={meta.severity} value={buyboxStateLabel(t, buyboxState)} />;
};

export default BuyboxStateBadge;
